import { useCallback, useMemo, useState } from "react";
import { useDataTableSort } from "hooks/useDataTableSort";
import type { DataTableGetSortValue, DataTableSortDirection } from "utils/dataTableSort";

export interface LocalDataTablePaginationOptions<T = unknown> {
  initialPage?: number;
  initialPageSize?: number;
  initialSortColumnId?: string | null;
  initialSortDirection?: DataTableSortDirection;
  getSortValue?: DataTableGetSortValue<T>;
}

export interface LocalDataTablePaginationResult<T> {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  paginatedItems: T[];
  setPage: (_page: number) => void;
  setPageSize: (_size: number) => void;
  resetPage: () => void;
  sortColumnId: string | null;
  sortDirection: DataTableSortDirection;
  onSortColumn: (_columnId: string) => void;
}

export function useLocalDataTablePagination<T>(
  items: readonly T[],
  options?: LocalDataTablePaginationOptions<T>
): LocalDataTablePaginationResult<T> {
  const [page, setPage] = useState(options?.initialPage ?? 0);
  const [pageSize, setPageSize] = useState(options?.initialPageSize ?? 10);

  const { sortColumnId, sortDirection, onSortColumn, sortItems } = useDataTableSort<T>({
    initialColumnId: options?.initialSortColumnId ?? null,
    initialDirection: options?.initialSortDirection ?? "asc",
    getSortValue: options?.getSortValue,
  });

  const handleSortColumn = useCallback(
    (columnId: string) => {
      onSortColumn(columnId);
      setPage(0);
    },
    [onSortColumn]
  );

  const sortedItems = useMemo(() => sortItems(items), [items, sortItems]);

  const totalItems = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const safePage = Math.min(Math.max(0, page), Math.max(0, totalPages - 1));
  const paginatedItems = useMemo(() => {
    const start = safePage * pageSize;
    return sortedItems.slice(start, start + pageSize);
  }, [sortedItems, pageSize, safePage]);

  const resetPage = () => setPage(0);

  return {
    page: safePage,
    pageSize,
    totalItems,
    totalPages,
    paginatedItems,
    setPage,
    setPageSize,
    resetPage,
    sortColumnId,
    sortDirection,
    onSortColumn: handleSortColumn,
  };
}
